import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SlDrawer } from "@shoelace-style/shoelace/dist/react";

import API from "../../services/api";
import TableLoader from "../../shared/TableLoader";
import { Toast } from "../../shared/Toast";
import { formatDateWithTimeYear } from "../../utils/dateFormat";

/**
 * CST-only per-order config that drives the sales-facing order report.
 * Persisted to the `order_report_metrics` table via /order_report_metrics/*.
 * The UI calls this "Sales Report Metrics"; the backend module is
 * order_report_metrics (a `sales_report_*` name would collide with the
 * unrelated /sales_reports revenue module).
 */

// A cleared <input type="number"> yields "", which yup would coerce to NaN and
// report as a typeError. Map empty back to null instead.
const optionalNumber = (label) =>
  yup
    .number()
    .transform((value, original) => (original === "" || original === null ? null : value))
    .typeError(`${label} must be a number`)
    .min(0, `${label} cannot be negative`)
    .nullable();

const schema = yup.object({
  reach_frequency: optionalNumber("Reach/Frequency"),
  spike_percentage: optionalNumber("Spike percentage").max(100, "Cannot exceed 100"),
  view_percentage: optionalNumber("View percentage").max(100, "Cannot exceed 100"),
  show_budget: yup.boolean(),
  show_stats: yup.boolean(),
  show_geo: yup.boolean(),
  include_today: yup.boolean(),
});

const defaultValues = {
  reach_frequency: "",
  spike_percentage: "",
  // CST-facing default: views percentage starts at 75%.
  view_percentage: 75,
  // Show Budget and Show Stats start ticked for a new config; an existing
  // config's stored values win, via reset() in the prefill effect.
  show_budget: true,
  show_stats: true,
  // Serving locations are opt-in: the section is derived from the line item
  // targets, so it is only worth showing when the order is geo-targeted.
  show_geo: false,
  views: false,
  // Unticked by default: today is still accumulating, so a part-delivered last
  // row reads as a sudden drop to an advertiser.
  include_today: false,
};

// Unix seconds -> DD/MM/YYYY, matching the report.
const formatDMY = (seconds) => {
  if (!seconds) return "-";
  const d = new Date(seconds * 1000);
  const pad = (v) => String(v).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const LockedNote = () => (
  <span className="text-xs text-slate-500">
    Set once and cannot be changed &mdash; reports already use this value.
  </span>
);

function SalesReportMetrics({ order, onClose }) {
  const [loading, setLoading] = useState(true);
  const [existing, setExisting] = useState(false);
  // reach_frequency / spike_percentage / view_percentage are one-time inputs:
  // the server adds an agreed random offset on first save and freezes them
  // thereafter, so a salesperson never sees a figure move. The server decides
  // which are locked; we only reflect it.
  const [locked, setLocked] = useState([]);
  const isLocked = (field) => locked.includes(field);
  // One row per day of the order's run, generated server-side on first save and
  // frozen. Read-only here: the CST sees exactly what the report will quote.
  const [dailyMetrics, setDailyMetrics] = useState([]);
  // Append-only change history, newest first and capped server-side.
  const [logs, setLogs] = useState([]);

  // Serving Locations: the areas the order's line items target, each with the
  // share of impressions it carries in the report. Seeded from daily actives,
  // then owned by the CST - the report renders these percentages verbatim and
  // recalculates nothing, so this is the only place the split is decided.
  const [geoRows, setGeoRows] = useState([]);
  // Saved areas the targets no longer cover. The report still shows them until
  // the CST re-syncs, so they are surfaced rather than silently dropped.
  const [geoRemoved, setGeoRemoved] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoLoaded, setGeoLoaded] = useState(false);
  const [geoWeighted, setGeoWeighted] = useState(true);
  // DFP line items serve at language level only, so there is no targeting to
  // derive areas from - the CST types them. Shown only when the order has any.
  const [dfpLineItems, setDfpLineItems] = useState(0);
  const [dfpRows, setDfpRows] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: yupResolver(schema), defaultValues, mode: "onBlur" });

  const showGeo = watch("show_geo");

  // Percentages are held to 2dp, so compare the rounded sum rather than the
  // float one - 33.33 x 3 is 99.99, not 100, and that has to read as invalid.
  const sumPercent = (rows) =>
    Math.round(rows.reduce((sum, row) => sum + (Number(row.percentage) || 0), 0) * 100) / 100;

  const geoTotal = sumPercent(geoRows);
  const dfpTotal = sumPercent(dfpRows);

  // Each table splits the impressions its own line items delivered, so each
  // totals 100 in its own right rather than 100 between them.
  const geoBalanced = !showGeo || !geoRows.length || Math.abs(geoTotal - 100) < 0.005;
  // A DFP line item delivers impressions the derived areas cannot account for,
  // so an order with DFP needs a DFP split or those impressions go missing from
  // the section while still counting in the grand total.
  const dfpBalanced =
    !showGeo || !dfpLineItems || (dfpRows.length && Math.abs(dfpTotal - 100) < 0.005);

  // ── prefill ──

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await API({
          url: "/order_report_metrics/get-metrics",
          method: "POST",
          responseType: "json",
          data: { order_id: order.order_id },
        });
        if (cancelled) return;

        const config = res?.code === 200 ? res.data?.config : null;
        if (res?.code === 200) {
          setLocked(res.data?.locked || []);
          setLogs(res.data?.logs || []);
        }
        if (!config) return;

        setExisting(true);
        setDailyMetrics(Array.isArray(config.daily_metrics) ? config.daily_metrics : []);
        reset({
          reach_frequency: config.reach_frequency ?? "",
          spike_percentage: config.spike_percentage ?? "",
          view_percentage: config.view_percentage ?? "",
          show_budget: !!config.show_budget,
          show_stats: !!config.show_stats,
          show_geo: !!config.show_geo,
          views: !!config.views,
          // Saved before this existed -> undefined, which meant included.
          include_today: config.include_today !== false,
        });
      } catch (error) {
        console.error("Error loading sales report metrics", error);
        if (!cancelled) Toast("error", "Could not load existing metrics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.order_id]);

  // ── serving locations ──

  // Loaded when Show Geo is first switched on, and kept afterwards so toggling
  // the box off and on again does not discard half-made edits.
  useEffect(() => {
    if (!showGeo || geoLoaded || geoLoading || loading) return;

    let cancelled = false;
    setGeoLoading(true);
    (async () => {
      try {
        const res = await API({
          url: "/order_report_metrics/get-locations",
          method: "POST",
          responseType: "json",
          data: { order_id: order.order_id },
        });
        if (cancelled) return;
        if (res?.code === 200) {
          setGeoRows(res.data?.rows || []);
          setGeoRemoved(res.data?.removed || []);
          setGeoWeighted(res.data?.weighted !== false);
          setDfpLineItems(res.data?.dfp_line_items || 0);
          setDfpRows(res.data?.dfp_rows || []);
          setGeoLoaded(true);
        } else {
          Toast("error", res?.message || "Could not load serving locations");
        }
      } catch (error) {
        console.error("Error loading serving locations", error);
        if (!cancelled) Toast("error", "Could not load serving locations");
      } finally {
        if (!cancelled) setGeoLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGeo, loading]);

  // Kept as the raw string while editing so a half-typed "12." does not get
  // rewritten under the cursor; it is coerced on save.
  const handleGeoChange = (index, value) => {
    setGeoRows((rows) =>
      rows.map((row, i) => (i === index ? { ...row, percentage: value } : row))
    );
  };

  const handleGeoReset = () => {
    setGeoRows((rows) =>
      rows.map((row) => ({ ...row, percentage: row.dau_percentage }))
    );
  };

  const handleDfpChange = (index, field, value) => {
    setDfpRows((rows) =>
      rows.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  // Kept as the raw string while editing so a half-typed "12." is not rewritten
  // under the cursor; coerced on save. Blank clears the day back to the base
  // value, which is what the report falls back to.
  const handleDailyChange = (index, field, value) => {
    setDailyMetrics((rows) =>
      rows.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  // Mirrors the server's own bounds, so the CST is stopped here rather than by
  // a rejected save.
  const DAILY_BOUNDS = {
    reach_frequency: [0, Number.MAX_SAFE_INTEGER],
    impressions_spike: [0, 100],
    clicks_spike: [0, 100],
    view_percentage: [0, 100],
  };
  const dailyOutOfRange = dailyMetrics.some((row) =>
    Object.entries(DAILY_BOUNDS).some(([field, [min, max]]) => {
      const raw = row[field];
      if (raw === null || raw === undefined || raw === "") return false;
      const value = Number(raw);
      return Number.isNaN(value) || value < min || value > max;
    })
  );

  const handleDfpAdd = () =>
    setDfpRows((rows) => [...rows, { location: "", percentage: "" }]);

  const handleDfpRemove = (index) =>
    setDfpRows((rows) => rows.filter((_, i) => i !== index));

  // Drops the areas the targets no longer cover and re-seeds from daily
  // actives, which is the only way to clear a drift warning.
  const handleGeoResync = () => {
    setGeoRemoved([]);
    setGeoRows((rows) =>
      rows.map((row) => ({ ...row, percentage: row.dau_percentage, status: "ok" }))
    );
  };

  // ── submit ──

  const onSubmit = async (data) => {
    // The report multiplies these against the order's total impressions, so a
    // set that does not add up to 100 would put location rows that do not
    // reconcile with the grand total in front of an advertiser. The server
    // refuses it too; this is just the earlier, clearer stop.
    if (!geoBalanced) {
      Toast("error", `Regular location percentages must total 100, currently ${geoTotal}`);
      return;
    }
    if (dailyOutOfRange) {
      Toast("error", "Check the daily values - percentages must be 0-100 and reach cannot be negative");
      return;
    }
    if (!dfpBalanced) {
      Toast(
        "error",
        dfpRows.length
          ? `DFP location percentages must total 100, currently ${dfpTotal}`
          : "This order has DFP line items - add at least one DFP location"
      );
      return;
    }

    try {
      const res = await API({
        url: "/order_report_metrics/save-metrics",
        method: "POST",
        responseType: "json",
        data: {
          order_id: order.order_id,
          reach_frequency: data.reach_frequency ?? null,
          spike_percentage: data.spike_percentage ?? null,
          view_percentage: data.view_percentage ?? null,
          show_budget: !!data.show_budget,
          show_stats: !!data.show_stats,
          show_geo: !!data.show_geo,
          views: !!data.views,
          include_today: !!data.include_today,
          // A date sent here is authoritative for all three of its fields; a
          // blank clears that field back to the base value.
          daily_metrics: dailyMetrics.length
            ? dailyMetrics.map((row) => ({
                date: Number(row.date),
                reach_frequency:
                  row.reach_frequency === "" || row.reach_frequency === null
                    ? null
                    : Number(row.reach_frequency),
                impressions_spike:
                  row.impressions_spike === "" || row.impressions_spike === null
                    ? null
                    : Number(row.impressions_spike),
                clicks_spike:
                  row.clicks_spike === "" || row.clicks_spike === null
                    ? null
                    : Number(row.clicks_spike),
                view_percentage:
                  row.view_percentage === "" || row.view_percentage === null
                    ? null
                    : Number(row.view_percentage),
              }))
            : null,
          geo_locations:
            data.show_geo && geoRows.length
              ? geoRows.map((row) => ({
                  level: row.level,
                  location: row.location,
                  id: row.id,
                  lang_id: row.lang_id,
                  percentage: Number(row.percentage) || 0,
                }))
              : null,
          geo_dfp_locations:
            data.show_geo && dfpRows.length
              ? dfpRows
                  .filter((row) => String(row.location || "").trim())
                  .map((row) => ({
                    location: String(row.location).trim(),
                    percentage: Number(row.percentage) || 0,
                  }))
              : null,
        },
      });
      if (res?.code === 200) {
        Toast("success", "Sales report metrics saved");
        onClose();
      } else {
        Toast("error", res?.message || "Failed to save sales report metrics");
      }
    } catch (error) {
      console.error("Error saving sales report metrics", error);
      Toast("error", "Failed to save sales report metrics");
    }
  };

  return (
    <SlDrawer
      open={order?.order_id ? true : false}
      label={`Sales Report Metrics${order?.order_name ? ` - ${order.order_name}` : ""}`}
      style={{ "--size": window.innerWidth <= 767 ? "100vw" : "40vw" }}
      onSlRequestClose={(event) => {
        // Don't let a stray backdrop click discard a half-filled form.
        if (event.detail.source === "overlay") event.preventDefault();
      }}
      onSlAfterHide={(event) => {
        event.stopPropagation();
        onClose();
      }}
    >
      {loading ? (
        <TableLoader />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm text-slate-700">Reach / Frequency</label>
              <input
                type="number"
                min="0"
                step="0.01"
                {...register("reach_frequency")}
                className="form-control"
                placeholder="e.g. 3.5"
                disabled={isLocked("reach_frequency")}
              />
              {isLocked("reach_frequency") ? <LockedNote /> : null}
              {errors.reach_frequency && (
                <span className="text-xs text-red-600">{errors.reach_frequency.message}</span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm text-slate-700">Spike Percentage</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                {...register("spike_percentage")}
                className="form-control"
                placeholder="e.g. 12.5"
                disabled={isLocked("spike_percentage")}
              />
              {isLocked("spike_percentage") ? <LockedNote /> : null}
              {errors.spike_percentage && (
                <span className="text-xs text-red-600">{errors.spike_percentage.message}</span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm text-slate-700">View Percentage</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                {...register("view_percentage")}
                className="form-control"
                placeholder="e.g. 75"
                disabled={isLocked("view_percentage")}
              />
              {isLocked("view_percentage") ? <LockedNote /> : null}
              {errors.view_percentage && (
                <span className="text-xs text-red-600">{errors.view_percentage.message}</span>
              )}
            </div>
          </div>

          {dailyMetrics.length ? (
            <div className="rounded border border-slate-200 p-3">
              <h6 className="text-sm font-semibold text-slate-700 mb-1">
                Daily Values
              </h6>
              <p className="text-xs text-slate-500 mb-2">
                Each day of this order carries its own figure, generated from the values above
                and used by the report for that date. Impressions and clicks carry their own
                spike, so the two move independently. Edit any of them here; clearing a cell
                falls that day back to the value at the top.
              </p>
              <div className="overflow-y-auto" style={{ maxHeight: "260px" }}>
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th className="text-xs">Date</th>
                      <th className="text-xs text-end">Reach / Frequency</th>
                      <th className="text-xs text-end">Impressions Spike %</th>
                      <th className="text-xs text-end">Clicks Spike %</th>
                      <th className="text-xs text-end">View %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyMetrics.map((row, index) => (
                      <tr key={row.date}>
                        <td className="text-xs align-middle">{formatDMY(row.date)}</td>
                        {[
                          "reach_frequency",
                          "impressions_spike",
                          "clicks_spike",
                          "view_percentage",
                        ].map(
                          (field) => (
                            <td key={field}>
                              <input
                                type="number"
                                min="0"
                                max={field === "reach_frequency" ? undefined : 100}
                                step="0.0001"
                                className="form-control form-control-sm text-end"
                                value={row[field] ?? ""}
                                onChange={(event) =>
                                  handleDailyChange(index, field, event.target.value)
                                }
                              />
                            </td>
                          )
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-500 mb-0 mt-2">
                {dailyMetrics.length} day{dailyMetrics.length === 1 ? "" : "s"}
              </p>
            </div>
          ) : null}

          <div className="flex items-center gap-6 pt-2">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register("show_budget")}
                className="h-4 w-4 rounded border-slate-300 text-slate-700 focus:ring-slate-500"
                id="srm-show-budget"
              />
              <label htmlFor="srm-show-budget" className="text-sm text-slate-700">
                Show Budget
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register("show_stats")}
                className="h-4 w-4 rounded border-slate-300 text-slate-700 focus:ring-slate-500"
                id="srm-show-stats"
              />
              <label htmlFor="srm-show-stats" className="text-sm text-slate-700">
                Show Stats
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register("views")}
                className="h-4 w-4 rounded border-slate-300 text-slate-700 focus:ring-slate-500"
                id="srm-views"
              />
              <label htmlFor="srm-views" className="text-sm text-slate-700">
                Views
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register("show_geo")}
                className="h-4 w-4 rounded border-slate-300 text-slate-700 focus:ring-slate-500"
                id="srm-show-geo"
              />
              <label htmlFor="srm-show-geo" className="text-sm text-slate-700">
                Show Geo
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register("include_today")}
                className="h-4 w-4 rounded border-slate-300 text-slate-700 focus:ring-slate-500"
                id="srm-include-today"
              />
              <label htmlFor="srm-include-today" className="text-sm text-slate-700">
                Include Today
              </label>
            </div>
          </div>

          <p className="text-xs text-slate-500 mb-0">
            Leave Include Today off while the campaign is running: today&apos;s figures are
            still being collected, so the last row would read as a drop. It affects the whole
            report &mdash; the daily rows, the totals and the location split.
          </p>

          <p className="text-xs text-slate-500 mb-0">
            Show Geo adds a Serving Locations section to the report. The areas come from this
            order&apos;s line item targets; the split below is yours to set and the report uses
            it as given.
          </p>

          {showGeo ? (
            <div className="rounded border border-slate-200 p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <h6 className="text-sm font-semibold text-slate-700 mb-0">
                  Serving Locations &mdash; Regular
                </h6>
                {geoRows.length ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleGeoReset}
                  >
                    Reset to audience share
                  </button>
                ) : null}
              </div>

              {geoLoading ? (
                <TableLoader />
              ) : !geoRows.length ? (
                <p className="text-xs text-slate-500 mb-0">
                  This order&apos;s running, paused and completed line items have no location
                  targeting, so there is nothing to split.
                </p>
              ) : (
                <>
                  {!geoWeighted ? (
                    <p className="text-xs text-amber-700 mb-2">
                      Audience figures were unavailable, so the split below starts out even.
                    </p>
                  ) : null}

                  {geoRemoved.length || geoRows.some((row) => row.status === "new") ? (
                    <div className="text-xs text-amber-700 mb-2">
                      <div className="font-semibold">
                        Targets have changed since this was saved.
                      </div>
                      {geoRows.some((row) => row.status === "new") ? (
                        <div>
                          New, not yet in the report:{" "}
                          {geoRows
                            .filter((row) => row.status === "new")
                            .map((row) => row.location)
                            .join(", ")}
                        </div>
                      ) : null}
                      {geoRemoved.length ? (
                        <div>
                          No longer targeted, still in the report:{" "}
                          {geoRemoved.map((row) => row.location).join(", ")}
                        </div>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary mt-1"
                        onClick={handleGeoResync}
                      >
                        Re-sync from targets
                      </button>
                    </div>
                  ) : null}

                  <div className="overflow-y-auto" style={{ maxHeight: "320px" }}>
                    <table className="table table-sm mb-2">
                      <thead>
                        <tr>
                          <th className="text-xs">Level</th>
                          <th className="text-xs">Location</th>
                          <th className="text-xs text-end" style={{ width: "120px" }}>
                            Contribution %
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {geoRows.map((row, index) => (
                          <tr key={`${row.level}-${row.lang_id}-${row.id}`}>
                            <td className="text-xs align-middle text-slate-500">{row.level}</td>
                            <td className="text-xs align-middle">
                              {row.location}
                              {row.status === "new" ? (
                                <span className="text-amber-700"> (new)</span>
                              ) : null}
                            </td>
                            <td>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                className="form-control form-control-sm text-end"
                                value={row.percentage}
                                onChange={(event) =>
                                  handleGeoChange(index, event.target.value)
                                }
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500 text-xs">
                      {geoRows.length} location{geoRows.length === 1 ? "" : "s"}
                    </span>
                    <span className={geoBalanced ? "text-emerald-700" : "text-red-600"}>
                      Total {geoTotal.toFixed(2)}%
                      {geoBalanced ? "" : " — must be 100 to save"}
                    </span>
                  </div>
                </>
              )}
            </div>
          ) : null}

          {showGeo && !geoLoading && dfpLineItems ? (
            <div className="rounded border border-slate-200 p-3">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h6 className="text-sm font-semibold text-slate-700 mb-0">
                  Serving Locations &mdash; DFP
                </h6>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleDfpAdd}
                >
                  Add location
                </button>
              </div>

              <p className="text-xs text-slate-500 mb-2">
                {dfpLineItems} DFP line item{dfpLineItems === 1 ? "" : "s"} on this order. DFP
                serves at language level only, so these areas cannot be read from the targets
                &mdash; type them here. They split what the DFP line items delivered, not the
                order&apos;s whole total.
              </p>

              {!dfpRows.length ? (
                <p className="text-xs text-red-600 mb-0">
                  Add at least one DFP location, or those impressions will be missing from the
                  report.
                </p>
              ) : (
                <>
                  <table className="table table-sm mb-2">
                    <thead>
                      <tr>
                        <th className="text-xs">Location</th>
                        <th className="text-xs text-end" style={{ width: "120px" }}>
                          Contribution %
                        </th>
                        <th style={{ width: "40px" }} />
                      </tr>
                    </thead>
                    <tbody>
                      {dfpRows.map((row, index) => (
                        // Index keyed on purpose: these rows have no id, and the
                        // name is what the CST is still typing.
                        <tr key={index}>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="e.g. Andhra Pradesh"
                              value={row.location || ""}
                              onChange={(event) =>
                                handleDfpChange(index, "location", event.target.value)
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              className="form-control form-control-sm text-end"
                              value={row.percentage}
                              onChange={(event) =>
                                handleDfpChange(index, "percentage", event.target.value)
                              }
                            />
                          </td>
                          <td className="align-middle text-center">
                            <sl-icon-button
                              library="lucide"
                              name="trash-2"
                              onClick={() => handleDfpRemove(index)}
                            ></sl-icon-button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500 text-xs">
                      {dfpRows.length} location{dfpRows.length === 1 ? "" : "s"}
                    </span>
                    <span className={dfpBalanced ? "text-emerald-700" : "text-red-600"}>
                      Total {dfpTotal.toFixed(2)}%
                      {dfpBalanced ? "" : " — must be 100 to save"}
                    </span>
                  </div>
                </>
              )}
            </div>
          ) : null}

          {logs.length ? (
            <div className="rounded border border-slate-200 p-3">
              <h6 className="text-sm font-semibold text-slate-700 mb-1">History</h6>
              <p className="text-xs text-slate-500 mb-2">
                Who changed what, newest first. Only fields that actually moved are listed.
              </p>
              <div className="overflow-y-auto" style={{ maxHeight: "240px" }}>
                {logs.map((entry, index) => (
                  <div key={`${entry.time}-${index}`} className="mb-3">
                    <div className="text-xs font-semibold text-slate-700">
                      {formatDateWithTimeYear(entry.time)}
                      {entry.user_name ? ` — ${entry.user_name}` : ""}
                    </div>
                    <table className="table table-sm mb-0">
                      <tbody>
                        {(entry.update || []).map((change, i) => (
                          <tr key={i}>
                            <td className="text-xs text-slate-500">{change.type}</td>
                            <td className="text-xs text-end text-slate-500">
                              {change.previous}
                            </td>
                            <td className="text-xs text-center text-slate-400">&rarr;</td>
                            <td className="text-xs">{change.updated}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mb-0">
                {logs.length} change{logs.length === 1 ? "" : "s"}
              </p>
            </div>
          ) : null}

          <div className="flex justify-end gap-2 pt-4">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                isSubmitting || geoLoading || !geoBalanced || !dfpBalanced || dailyOutOfRange
              }
            >
              {isSubmitting ? "Saving..." : existing ? "Update" : "Save"}
            </button>
          </div>
        </form>
      )}
    </SlDrawer>
  );
}

export default SalesReportMetrics;
